import { useState, useCallback, useEffect } from "react";
import IconButton from "components/Buttons/IconButton";
import { ReactSelect } from "components/FormFields/DropdownField";
import { AddCircle20Regular } from "@fluentui/react-icons";
import { IoTrashOutline } from "react-icons/io5";
import { http } from "middleware/apiMiddleware";
import { subscriptionAdvisor, subscriptionAdvisorUsageCheck } from "constants/apiUrls";
import { toast } from "react-hot-toast";
import ThinkingModal from "components/Common/ThinkingModal";

interface SmartAdvisorModalProps {
  onSubmit: (data: any) => void;
  businessUuid: string;
  onUsageUpdate?: (usageData: { maxLimit: number; usageCount: number; disableUsage: boolean }) => void;
}

const SmartAdvisorModal: React.FC<SmartAdvisorModalProps> = ({ onSubmit, businessUuid, onUsageUpdate }) => {
  const [formData, setFormData] = useState({
    itemToPromote: "",
    typicalPrice: "",
    timePreset: null,
    daysOfWeek: null,
    subscriptionCycle: { value: "MONTHLY", label: "Monthly" },
  });

  const [storeRules, setStoreRules] = useState<string[]>([]);
  const [ruleInputs, setRuleInputs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [usageData, setUsageData] = useState<{ maxLimit: number; usageCount: number; disableUsage: boolean }>({
    maxLimit: 10,
    usageCount: 0,
    disableUsage: false
  });

  // Generate thinking steps based on form data
  const getThinkingSteps = useCallback(() => {
    const steps: string[] = [];
    
    steps.push("Processing Steps:\n");
    steps.push("1. Synthesize Business & User Input");
    steps.push(`   a. Analyzing business profile and location data.`);
    steps.push(`   b. Evaluating user goals for promoting ${formData.itemToPromote || "items"} at $${formData.typicalPrice || "X"}.`);
    
    if (formData.timePreset || formData.daysOfWeek) {
      const timeStr = formData.timePreset ? (formData.timePreset as any).label : "";
      const daysStr = formData.daysOfWeek ? (formData.daysOfWeek as any).label : "";
      steps.push(`   c. Noting time boundaries: ${daysStr}${daysStr && timeStr ? ", " : ""}${timeStr}.`);
    }
    
    steps.push("\n2. Simulating a Hyper-Local Market Analysis");
    steps.push("   a. Performing competitive analysis in your area.");
    steps.push("   b. Analyzing pricing strategies and customer behavior patterns.");
    steps.push("   c. Evaluating optimal subscription cycles and item quantities.");
    
    steps.push("\n3. Generating Subscription Recommendations");
    steps.push("   a. Creating pricing models based on item value and market data.");
    steps.push("   b. Optimizing subscription tiers for customer retention.");
    steps.push("   c. Calculating profit margins and break-even points.");
    
    steps.push("\n4. Applying Store Rules & Constraints");
    if (storeRules.length > 0) {
      steps.push("   a. Incorporating your custom store rules into suggestions.");
      steps.push("   b. Ensuring compliance with operational constraints.");
    } else {
      steps.push("   a. Analyzing standard operational constraints.");
      steps.push("   b. Optimizing for typical business hours and patterns.");
    }
    
    steps.push("\n5. Finalizing Recommendations");
    steps.push("   a. Validating subscription parameters and pricing.");
    steps.push("   b. Preparing detailed justifications for each suggestion.");
    steps.push("   c. Generating final recommendations...");
    
    return steps;
  }, [formData, storeRules]);

  const timePresetOptions = [
    { value: "morning", label: "Morning (6 AM - 12 PM)" },
    { value: "afternoon", label: "Afternoon (12 PM - 5 PM)" },
    { value: "evening", label: "Evening (5 PM - 10 PM)" },
    { value: "all_day", label: "All Day" },
    { value: "custom", label: "Custom" },
  ];

  const daysOfWeekOptions = [
    { value: "all_days", label: "All days" },
    { value: "weekdays", label: "Weekdays (Mon-Fri)" },
    { value: "weekends", label: "Weekends (Sat-Sun)" },
    { value: "custom", label: "Custom days" },
  ];

  const subscriptionCycleOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
    { value: "3_MONTH", label: "3 Months" },
    { value: "6_MONTH", label: "6 Months" },
    { value: "YEARLY", label: "Yearly" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);

      // Save any pending rules
      const validRules = ruleInputs.filter((input) => input.trim());
      const allRules = [...storeRules, ...validRules];

      // Build time boundaries string
      let timeBoundaries = "";
      if (formData.daysOfWeek && formData.timePreset) {
        const daysStr = (formData.daysOfWeek as any).label || "";
        const timeStr = (formData.timePreset as any).label || "";
        timeBoundaries = `${daysStr}, ${timeStr}`;
      } else if (formData.daysOfWeek) {
        timeBoundaries = (formData.daysOfWeek as any).label || "";
      } else if (formData.timePreset) {
        timeBoundaries = (formData.timePreset as any).label || "";
      }

      // Prepare payload according to API documentation
      const payload = {
        item_to_promote: formData.itemToPromote,
        retail_price: parseFloat(formData.typicalPrice),
        store_rules: allRules.join(", "), // Join rules into a single string
        time_boundaries: timeBoundaries,
      };

      const { data, error } = await http.post(
        subscriptionAdvisor(businessUuid),
        payload
      );

      if (error) {
        toast.error("Failed to generate suggestions. Please try again later.");
        return;
      }

      if (data?.success === false) {
        if (data?.error === "AI service is not configured properly") {
          toast.error("AI Advisor service is currently unavailable. Please try creating a subscription manually or contact support.");
        } else {
          toast.error(data?.error || "Failed to generate suggestions");
        }
        return;
      }

      if (data?.suggestions && data.suggestions.length > 0) {
        toast.success(`${data.count} suggestions generated successfully`);
        // Pass suggestions to parent component
        onSubmit(data.suggestions);
        // Check usage after successful generation to update the counter
        await checkUsage();
      } else {
        toast.error("No suggestions were generated. Please try again with different parameters.");
      }
    } catch (error) {
      toast.error("Unexpected error occurred while generating suggestions");
    } finally {
      setIsLoading(false);
    }
  };

  const addRuleField = () => {
    setRuleInputs([...ruleInputs, ""]);
  };

  const updateRuleInput = (index: number, value: string) => {
    const updated = [...ruleInputs];
    updated[index] = value;
    setRuleInputs(updated);
  };

  const removeRuleInput = (index: number) => {
    setRuleInputs(ruleInputs.filter((_, i) => i !== index));
  };

  const deleteRule = (index: number) => {
    setStoreRules(storeRules.filter((_, i) => i !== index));
  };

  const checkUsage = async () => {
    try {
      const { data, error } = await http.get(subscriptionAdvisorUsageCheck(businessUuid));
      
      if (error) {
        return;
      }

      if (data) {
        setUsageData(data);
        if (onUsageUpdate) {
          onUsageUpdate(data);
        }
      }
    } catch (error) {
      // Silent fail - usage check is optional
    }
  };

  // Check usage on component mount
  useEffect(() => {
    if (businessUuid) {
      checkUsage();
    }
  }, [businessUuid]);

  // Check if required fields are filled
  const isFormValid = formData.itemToPromote.trim() !== "" && formData.typicalPrice.trim() !== "";
  
  // Check if usage limit is reached
  const isUsageLimitReached = usageData.disableUsage || usageData.usageCount >= usageData.maxLimit;

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <style>
        {`
          /* Hide number input spinners */
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
          }

          /* Placeholder styling */
          input::placeholder,
          textarea::placeholder {
            font-family: 'Poppins', sans-serif;
            font-weight: 400;
            font-size: 14px;
            line-height: 100%;
            letter-spacing: 0%;
            opacity: 1;
          }
        `}
      </style>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-5">
        {/* Description Text */}
        <p className="font-poppins text-sm">
          Use Smart Advisor to generate subscription plans rather than creating manually.
        </p>

        {/* Item to Promote & Typical Price */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Item to Promote
            </label>
            <input
              type="text"
              placeholder="E.g. Coffee, Tea, Pastries"
              value={formData.itemToPromote}
              onChange={(e) =>
                setFormData({ ...formData, itemToPromote: e.target.value })
              }
              className="block w-full h-[40px] px-4 py-[10px] text-gray-900 bg-white border border-[#EDEDF0] focus:ring-primary focus:border-primary focus:outline-none font-poppins text-base md:text-sm"
              style={{ borderRadius: '4px' }}
            />
          </div>

          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Typical Price of the Item
            </label>
            <div className="flex">
              <div className="flex items-center justify-center w-[42px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                <span className="text-gray-900 font-medium">$</span>
              </div>
              <input
                type="number"
                placeholder="E.g. $3, $5"
                value={formData.typicalPrice}
                onChange={(e) =>
                  setFormData({ ...formData, typicalPrice: e.target.value })
                }
                className="block w-full h-[40px] px-4 py-[10px] text-gray-900 bg-white border border-l-0 border-[#EDEDF0] focus:ring-primary focus:border-primary focus:outline-none font-poppins text-base md:text-sm rounded-r"
                style={{ borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        {/* Target "When" Section */}
        <div>
          <h3 
            className="block mb-4 font-poppins leading-none"
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: '#0B1215'
            }}
          >
            Target "When" <span className="text-gray-500 font-normal">(optional)</span>
          </h3>
          
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1">
              <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
                Time Presets
              </label>
              <ReactSelect
                value={formData.timePreset}
                options={timePresetOptions}
                placeholder="Select Time Preset"
                isMulti={false}
                closeMenuOnSelect
                onChange={(selectedOption: any) => {
                  setFormData({ ...formData, timePreset: selectedOption });
                }}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, maxHeight: "300px" }),
                  menuList: (base) => ({ ...base, maxHeight: "300px" }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#FFFFFF',
                    borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                    minHeight: '40px',
                    height: '40px',
                    borderRadius: '4px',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: '40px',
                    padding: '0 8px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: '0',
                    padding: '0',
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '40px',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#9CA3AF',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#6B7280',
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#000000',
                    '& svg': {
                      width: '16px',
                      height: '16px',
                    },
                  }),
                }}
              />
            </div>

            <div className="flex-1">
              <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
                Days of the week
              </label>
              <ReactSelect
                value={formData.daysOfWeek}
                options={daysOfWeekOptions}
                placeholder="Select days of the week for your promotion"
                isMulti={false}
                closeMenuOnSelect
                onChange={(selectedOption: any) => {
                  setFormData({ ...formData, daysOfWeek: selectedOption });
                }}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, maxHeight: "300px" }),
                  menuList: (base) => ({ ...base, maxHeight: "300px" }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#FFFFFF',
                    borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                    minHeight: '40px',
                    height: '40px',
                    borderRadius: '4px',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: '40px',
                    padding: '0 8px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: '0',
                    padding: '0',
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '40px',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#9CA3AF',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#6B7280',
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#000000',
                    '& svg': {
                      width: '16px',
                      height: '16px',
                    },
                  }),
                }}
              />
            </div>
          </div>
        </div>

        {/* Subscription Cycle */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Subscription Cycle
            </label>
            <ReactSelect
              value={formData.subscriptionCycle}
              options={subscriptionCycleOptions}
              placeholder="Monthly"
              isMulti={false}
              closeMenuOnSelect
              isOptionDisabled={(option: any) => option.value !== "MONTHLY"}
              onChange={(selectedOption: any) => {
                setFormData({ ...formData, subscriptionCycle: selectedOption });
              }}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, maxHeight: "300px" }),
                menuList: (base) => ({ ...base, maxHeight: "300px" }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: '#F6F5FC',
                  borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                  minHeight: '40px',
                  height: '40px',
                  borderRadius: '4px',
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: '40px',
                  padding: '0 8px',
                }),
                input: (base) => ({
                  ...base,
                  margin: '0',
                  padding: '0',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: '40px',
                }),
                placeholder: (base) => ({
                  ...base,
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#9CA3AF',
                }),
                singleValue: (base) => ({
                  ...base,
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#6B7280',
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isDisabled
                    ? '#F3F4F6'
                    : state.isSelected
                    ? '#6366F1'
                    : state.isFocused
                    ? '#E0E7FF'
                    : 'white',
                  color: state.isDisabled
                    ? '#9CA3AF'
                    : state.isSelected
                    ? 'white'
                    : '#1F2937',
                  cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                  opacity: state.isDisabled ? 0.6 : 1,
                  '&:active': {
                    backgroundColor: state.isDisabled ? '#F3F4F6' : '#C7D2FE',
                  },
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: '#000000',
                  '& svg': {
                    width: '16px',
                    height: '16px',
                  },
                }),
              }}
            />
          </div>
          <div className="flex-1 hidden md:block"></div>
        </div>

        {/* Store Rules Section */}
        <div>
          <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
            Store Rules
          </label>
          
          {/* Display saved rules */}
          {storeRules.length > 0 && (
            <div className="mb-3 flex flex-col gap-[10px]">
              {storeRules.map((rule, index) => (
                <div
                  key={index}
                  className="flex items-center gap-[10px]"
                >
                  <div className="flex flex-1">
                    <div className="flex items-center justify-center w-[41px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                      <span className="text-gray-900 font-medium text-base sm:text-lg">{index + 1}.</span>
                    </div>
                    <input
                      type="text"
                      value={rule}
                      readOnly
                      className="bg-white border border-l-0 border-[#EDEDF0] px-4 py-[10px] text-gray-900 focus:ring-primary focus:border-primary focus:outline-none flex-1 h-[40px] max-w-full rounded-r font-poppins text-base md:text-sm"
                      style={{ borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}
                    />
                  </div>
                  <IconButton
                    icon={<IoTrashOutline size={22} />}
                    onClick={() => deleteRule(index)}
                    variant="danger"
                    ariaLabel="Delete rule"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Rule input fields */}
          {ruleInputs.map((input, index) => (
            <div 
              key={index} 
              className="flex items-center mb-3 gap-[10px]"
            >
              <div className="flex flex-1">
                <div className="flex items-center justify-center w-[41px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                  <span className="text-gray-900 font-medium text-base sm:text-lg">{storeRules.length + index + 1}.</span>
                </div>
                <input
                  type="text"
                  placeholder="Enter store rule..."
                  value={input}
                  onChange={(e) => updateRuleInput(index, e.target.value)}
                  className="bg-white border border-l-0 border-[#EDEDF0] px-4 py-[10px] text-gray-900 focus:ring-primary focus:border-primary focus:outline-none flex-1 h-[40px] max-w-full rounded-r font-poppins text-base md:text-sm"
                  style={{ borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}
                />
              </div>
              <IconButton
                icon={<IoTrashOutline size={22} />}
                onClick={() => removeRuleInput(index)}
                variant="danger"
                ariaLabel="Remove rule"
              />
            </div>
          ))}

          {/* Add Rule Button */}
          <button
            type="button"
            onClick={addRuleField}
            className="flex items-center gap-2 font-poppins font-medium hover:opacity-80 transition-opacity"
            style={{
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: '#0A84FF',
              backgroundColor: 'transparent',
            }}
          >
            <AddCircle20Regular className="w-5 h-5" style={{ color: '#0A84FF' }} />
            <span>{ruleInputs.length > 0 ? 'Add Another Rule' : 'Add Rules'}</span>
          </button>
        </div>

        {/* Usage Limit Warning */}
        {isUsageLimitReached && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="font-poppins text-sm text-orange-700">
              You've reached your daily limit of {usageData.maxLimit} Smart Advisor generations. 
              The limit will reset in 24 hours. You can still create subscriptions manually using "Create blank".
            </p>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-start pt-2">
          <button
            type="button"
            onClick={() => {
              if (isFormValid && !isLoading && !isUsageLimitReached) {
                handleSubmit({ preventDefault: () => {} } as React.FormEvent);
              }
            }}
            disabled={!isFormValid || isLoading || isUsageLimitReached}
            className="flex items-center justify-center gap-2 px-5 py-3 bg-royalBlue text-white rounded-full transition-all duration-200 font-poppins font-medium hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed !leading-none !text-center"
            style={{
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0px',
              height: '45px',
              minWidth: '208px'
            }}
            title={isUsageLimitReached ? "Daily usage limit reached" : ""}
          >
            {isLoading ? "Generating..." : isUsageLimitReached ? "Limit Reached" : "Generate Suggestions"}
          </button>
        </div>
      </form>

      {/* Thinking Modal */}
      <ThinkingModal
        isOpen={isLoading}
        message="Smart Advisor is thinking..."
        getThinkingSteps={getThinkingSteps}
      />
    </div>
  );
};

export default SmartAdvisorModal;
